import { css } from "@emotion/react";
import styled from "@emotion/styled";

import { CloudinaryImg } from "src/components/CloudinaryImg";
import { Link } from "src/components/Link";
import { PageSection } from "src/components/layout/PageSection";
import { StyledNumberItem } from "src/pages/CryptoLanding/StepsSection";
import { SectionAnchor } from "src/pages/Legal/TermsOfService";
import { colorCssVars } from "src/theme/color";
import { cssForMediaSize, MediaSize } from "src/theme/mediaQueries";
import { spacing, verticalStackCss } from "src/theme/spacing";
import { FontWeight, MAX_TEXT_WIDTH, textSizeCss } from "src/theme/text";

const PAYMENT_IMAGE_CLOUDINARY_ID = "crypto_payment_image_ip2rdn";
const PAYMENT_IMAGE_HEIGHT = 400;

const steps = [
  {
    title:
      "Every.org uses multiple exchanges to receive cryptocurrency donations of Bitcoin, Ethereum, USD Coin, and more.",
    description:
      "For donations worth $100,000 or more in a token not supported on our site, donors can email crypto@every.org to request an address.",
  },
  {
    title:
      "Every.org instantly sends donors a tax-deductible receipt and handles compliance.",
    description:
      "By accepting crypto and granting out to nonprofits in cash, Every.org saves nonprofits from extra legal, accounting, or administrative work, such as filing IRS Form 8282 — so that nonprofits can focus on the mission.",
  },
  {
    title:
      "Our exchanges charge a flat 1% fee and will instantly convert the crypto donation to USD.",
    description:
      "Every.org does not charge any fees of our own. For donations worth over $100k, you have the option to email crypto@every.org to arrange for manual exchange and potentially a lower fee.",
  },
  {
    title:
      "Finally, we will grant the realized USD value of your crypto to the nonprofit you supported per our disbursement schedule.",
  },
];

const StyledListItem = styled.li`
  ${verticalStackCss.s};
  position: relative;
  padding-left: ${spacing.xxl};
  color: var(${colorCssVars.text.secondary});

  & > h4 {
    color: var(${colorCssVars.text.body});
    font-weight: ${FontWeight.REGULAR};
    ${textSizeCss.s};
  }

  & > p {
    ${textSizeCss.xs}
  }
`;

const TextContainer = styled.div`
  max-width: ${MAX_TEXT_WIDTH};
  ${verticalStackCss.m};
  padding-bottom: ${spacing.xl};
  ${cssForMediaSize({
    min: MediaSize.LARGE,
    css: css`
      padding-bottom: 0;
      padding-right: ${spacing.xl};
    `,
  })}
`;

const StyledCloudinaryImg = styled.div`
  max-height: ${PAYMENT_IMAGE_HEIGHT}px;
  max-width: 100%;
`;

const pageSectionCss = css`
  display: grid;
  grid-template-columns: 1fr;
  align-items: center;
  ${cssForMediaSize({
    min: MediaSize.LARGE,
    css: css`
      grid-template-columns: 1fr 1fr;
    `,
  })}
`;

const pageSectionReverseCss = css`
  ${cssForMediaSize({
    min: MediaSize.LARGE,
    css: css`
      grid-auto-flow: dense;
    `,
  })}
  ${TextContainer} {
    ${cssForMediaSize({
      min: MediaSize.LARGE,
      css: css`
        grid-column: 2/3;
        padding-right: 0;
        padding-left: ${spacing.xl};
      `,
    })}
  }
  ${StyledCloudinaryImg} {
    width: 100%;
  }
`;
const Steps = () => (
  <li css={verticalStackCss.m}>
    {steps.map(({ title, description }, index) => (
      <StyledListItem key={title}>
        <StyledNumberItem>
          <span>{index + 1}</span>
        </StyledNumberItem>
        <h4>{title}</h4>
        {description && <p>{description}</p>}
      </StyledListItem>
    ))}
  </li>
);

export const HowItWorksSection: React.FCC<{ forGivers?: boolean }> = ({
  forGivers = false,
}) => {
  return (
    <PageSection
      contentCss={[pageSectionCss, forGivers && pageSectionReverseCss]}
    >
      <TextContainer>
        <SectionAnchor id="how-it-works" />
        <h3>How it works</h3>
        <Steps />
        <p css={{ paddingLeft: spacing.xxl }}>
          Go deeper with our{" "}
          <Link
            data-tname="CryptoHowItWorksSection__FAQ"
            to={
              forGivers
                ? "https://support.every.org/hc/en-us/sections/1500001365241-Cryptocurrency"
                : "https://support.every.org/hc/en-us/sections/1500001365421-Cryptocurrency"
            }
          >
            FAQ
          </Link>
          ,{" "}
          <Link
            data-tname="CryptoHowItWorksSection__BLOG"
            to="https://blog.every.org/crypto/"
          >
            blog series
          </Link>
          , or{" "}
          <Link
            data-tname="CryptoHowItWorksSection__DEMO"
            to="https://www.youtube.com/watch?v=MtiZk9-8i6w"
          >
            overview video
          </Link>
          .
        </p>
      </TextContainer>
      <StyledCloudinaryImg>
        <CloudinaryImg
          cloudinaryId={PAYMENT_IMAGE_CLOUDINARY_ID}
          dimensions={{ width: 1000, height: 690 }}
          alt="Donate crypto button"
        />
      </StyledCloudinaryImg>
    </PageSection>
  );
};
